import { Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';

import { StateManagementService } from './state-management.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TEST_CONST, TOAST_CONFIGURATION } from '../constants/constants';
import { ValidateTestResponseInterface } from '../model/validate-test';
import {
  StartTestData,
  StartTestInterface,
} from '../model/start-test-data';
import { UtilService } from './util.service';
import { SubmitAnswerBody, SubmitAnswerResponse } from '../model/submit-answer';
import { EndTestBody, EndTestInterface } from '../model/end-test';
import { MessageService } from 'primeng/api';
import { AddCandidateDetailBody, CandidateDetailResponse } from '../model/add-candidate';
import { FullscreenElement, FullscreenDocument } from '../model/full-screen-api-interface';
import { SnapshotResponse } from '../model/snapshot';
import { BrowserIdService } from './browser-id.service';

@Injectable({
  providedIn: 'root',
})
export class TestService {
  validateTestUrl: string = `${environment.BASE_URL}/${environment.BASE_API.Validate}`;
  testData: WritableSignal<StartTestData> = signal<StartTestData>(undefined);
  startTestUrl: string = `${environment.BASE_URL}/${environment.BASE_API.Start}`;
  submitAnswerUrl: string = `${environment.BASE_URL}/${environment.BASE_API.Question}`;
  candidateDetailUrl: string = `${environment.BASE_URL}/${environment.BASE_API.Details}`
  constructor (
    private router: Router,
    private stateManagementService: StateManagementService,
    private http: HttpClient,
    private utilService: UtilService,
    private messageService: MessageService,
    private browserIdService: BrowserIdService
  ) {}

  verifyTest(testCode: string): Observable<ValidateTestResponseInterface> {
    return this.http.get<ValidateTestResponseInterface>(
      `${this.validateTestUrl}/${testCode}`,
      {
        headers: new HttpHeaders({
          Authorization: TEST_CONST.auth_header,
        }),
      }
    );
  }

  startTest(password: string): Observable<StartTestInterface> {
    const testCode: string = this.testData().test_key;
    const testId: string = this.testData().test_id;
    const body: {
      DURATION: number;
      START_DATE: string;
      END_DATE: string;
      TEST_START_TIME: string;
      CANDIDATE_ID: string;
      PASSWORD: string;
      BROWSER_ID: string;
    } = {
      DURATION: parseInt(this.testData().duration),
      START_DATE: this.testData().start_date,
      END_DATE: this.testData().end_date,
      CANDIDATE_ID: this.testData().email,
      PASSWORD: password,
      TEST_START_TIME: this.utilService.formatDateTimeGeneric(undefined,true),
      BROWSER_ID: this.browserIdService.value
    };
    return this.http.post<StartTestInterface>(
      `${this.startTestUrl}/${testCode}/test/${testId}`,
      body,
      {
        headers: new HttpHeaders({
          Authorization: TEST_CONST.auth_header,
        }),
      }
    );
  }

  submitAnswer(questionId: string, selectedOptions:string[]):Observable<SubmitAnswerResponse> {
    const testId: string = this.testData().test_id;
    const candidateId: string = this.testData().email;
    const requestUrl: string = `${this.submitAnswerUrl}/${testId}/candidate/${candidateId}/submitanswer`;
    const body: SubmitAnswerBody = {
      QUESTION_ID: questionId,
      ANSWER_CHOICE: selectedOptions,
      SUBMIT_TIME: this.utilService.formatDateTimeGeneric(undefined,true)
    };
    return this.http.post<SubmitAnswerResponse>(requestUrl,body);
  }

  endTest(): Observable<EndTestInterface> {
    const testId: string = this.testData().test_id;
    const candidateId: string = this.testData().email;
    const requestUrl: string = `${this.submitAnswerUrl}/${testId}/candidate/${candidateId}/test`;
    const body: EndTestBody = {
      SUBMIT_TIME: this.utilService.formatDateTimeGeneric(undefined,true)
    };
    return this.http.post<EndTestInterface>(requestUrl,body);
  }

  submitTest(): void {
    this.stateManagementService.isSubmitTestRouteAllowed = true;
    this.utilService.isLoading.set(true);
    this.endTest().subscribe({
      next: (res) => {
        if (res.success) {
          this.messageService.add({
            severity: TOAST_CONFIGURATION.success,
            detail: res.message
          })
        } else {
          this.messageService.add({
            severity: TOAST_CONFIGURATION.info,
            detail: res.message
          })
        }
        this.utilService.isLoading.set(false);
      },
      error: (err) => {
        this.utilService.isLoading.set(false);
        this.messageService.add({
            severity: TOAST_CONFIGURATION.failed,
            summary: err.status,
            detail: err.message
        })
      }
    })
    this.router.navigate(['/submit-test']);
  }

  /**
   * Checks if the document is currently in fullscreen mode (cross-browser)
   */
  isFullscreen(): boolean {
    const doc = document as FullscreenDocument;
    return !!(
      document.fullscreenElement ||
      doc.mozFullScreenElement ||
      doc.webkitFullscreenElement ||
      doc.msFullscreenElement
    );
  }

  /**
   * Requests fullscreen mode (cross-browser with error handling)
   */
  async goFullscreen(isFullScreenEnforced: boolean): Promise<void> {
    if (!isFullScreenEnforced) {
      return;
    }

    const elem: FullscreenElement = document.documentElement;
    
    try {
      if (elem.requestFullscreen) {
        // Standard API (Chrome, Edge, Opera)
        await elem.requestFullscreen();
      } else if ((elem as any).mozRequestFullScreen) {
        // Firefox
        await (elem as any).mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        // Safari, older Chrome
        await elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        // IE/Edge (old)
        await elem.msRequestFullscreen();
      } else {
        console.warn('Fullscreen API is not supported in this browser');
        this.messageService.add({
          severity: TOAST_CONFIGURATION.warning,
          summary: 'Fullscreen Not Supported',
          detail: 'Your browser does not support fullscreen mode. Please use a modern browser.',
          closable: true
        });
      }
    } catch (error: any) {
      // User denied fullscreen or other error
      console.error('Fullscreen request failed:', error);
      if (error.name !== 'NotAllowedError') {
        this.messageService.add({
          severity: TOAST_CONFIGURATION.warning,
          summary: 'Fullscreen Error',
          detail: 'Unable to enter fullscreen mode. Please allow fullscreen permission and try again.',
          closable: true
        });
      }
      throw error;
    }
  }

  addCandidateDetail(candidateDetail: AddCandidateDetailBody): Observable<CandidateDetailResponse> {
    const token: string = this.testData().test_key;
    const test_id: string = this.testData().test_id;
    const url: string = `${this.candidateDetailUrl}/${token}/test/${test_id}`
    return this.http.post<CandidateDetailResponse>(url,candidateDetail)
  }

  getPreSignedUrl(): Observable<SnapshotResponse> {
    const testId: string = this.testData().test_id;
    const candidateId: string = this.testData().email;
    const url: string = `${this.submitAnswerUrl}/${testId}/candidate/${candidateId}/test/snapshots`
    return this.http.get<SnapshotResponse>(url);
  }
}
